import { Controller } from "@hotwired/stimulus"

// Progressive enhancement for a native <select>: a styled button, a menu, and a
// filter box once the list is long enough. This is what replaced select2.
//
//   <select class="form-control" data-controller="select">…</select>
//   <select data-controller="select" data-select-format-value="icon">…</select>
//   <select data-controller="select" data-select-format-value="color">…</select>
//
// The real <select> stays in the DOM and stays the source of truth — it is only
// visually hidden. That matters for three reasons:
//
//   * the form submits normally, with no hidden mirror field to keep in sync
//   * validation, labels and `form.reset()` keep working
//   * picking an option fires a real DOM `change` event, so Stimulus and plain
//     addEventListener hear it. select2 announced picks as a jQuery event that
//     never became a DOM event, which is why the old select.js had to translate
//     `select2:select` into a synthetic `change` by hand.
//
// Multi-selects are left alone — nothing here handles multiple values, and the
// native control is perfectly good for them.
export default class extends Controller {
    static values = {
        format: { type: String, default: "" },
        // Below this many options a filter box is noise. select2 used 10.
        searchFrom: { type: Number, default: 10 },
        // Lists this long refuse to render until the filter narrows them. The
        // icon picker holds 2681 options; drawing them all is ~5400 nodes plus
        // 2681 webfont glyph lookups, which locks the tab up. select2 avoided it
        // with minimumInputLength: 2 and this is the same guard.
        minInputFrom: { type: Number, default: 300 },
        minInput: { type: Number, default: 2 },
        // Even a filtered list gets capped — "a" still matches hundreds.
        maxResults: { type: Number, default: 150 },
    }

    connect() {
        if (this.element.multiple) return

        this.open = false
        this.#build()
        this.#syncLabel()

        // Someone else may set the value (a Stimulus controller, a reset).
        this.onExternalChange = () => this.#syncLabel()
        this.element.addEventListener("change", this.onExternalChange)
    }

    disconnect() {
        this.element.removeEventListener("change", this.onExternalChange)
        this.wrapper?.remove()
        this.element.classList.remove("select--enhanced")
        document.removeEventListener("click", this.onOutsideClick, true)
    }

    // ---- building ----------------------------------------------------------

    #build() {
        this.element.classList.add("select--enhanced")

        this.wrapper = document.createElement("div")
        this.wrapper.className = "select-ui"

        this.button = document.createElement("button")
        this.button.type = "button"
        this.button.className = "select-ui__toggle form-control"
        this.button.addEventListener("click", (e) => {
            e.preventDefault()
            this.toggle()
        })

        this.menu = document.createElement("div")
        this.menu.className = "select-ui__menu"
        this.menu.hidden = true

        if (this.#options().length >= this.searchFromValue) this.#addSearch()

        this.list = document.createElement("ul")
        this.list.className = "select-ui__list"
        this.menu.appendChild(this.list)
        // Deliberately not rendered here — the list is built on first open, so a
        // page carrying several long selects does not pay for any of them.

        this.wrapper.append(this.button, this.menu)
        this.element.after(this.wrapper)

        this.onOutsideClick = (event) => {
            if (this.open && !this.wrapper.contains(event.target)) this.close()
        }
        document.addEventListener("click", this.onOutsideClick, true)
    }

    #addSearch() {
        this.search = document.createElement("input")
        this.search.type = "text"
        this.search.className = "select-ui__search form-control"
        this.search.placeholder = "Suchen…"
        this.search.addEventListener("input", () => this.#renderOptions(this.search.value))
        // Typing must not submit the surrounding form.
        this.search.addEventListener("keydown", (e) => {
            if (e.key === "Enter") e.preventDefault()
        })
        this.menu.appendChild(this.search)
    }

    #options() {
        return Array.from(this.element.options)
    }

    #needsInput() {
        return this.#options().length >= this.minInputFromValue
    }

    #renderOptions(filter = "") {
        const needle = filter.trim().toLowerCase()
        this.list.replaceChildren()

        if (this.#needsInput() && needle.length < this.minInputValue) {
            this.list.appendChild(this.#hint(`Mindestens ${this.minInputValue} Zeichen eingeben…`))
            return
        }

        const matches = this.#options()
            .filter((option) => !needle || option.text.toLowerCase().includes(needle))

        if (matches.length === 0) {
            this.list.appendChild(this.#hint("Keine Treffer"))
            return
        }

        // Build off-document, then attach once: one reflow instead of N.
        const batch = document.createDocumentFragment()
        matches.slice(0, this.maxResultsValue).forEach((option) => {
            const item = document.createElement("li")
            item.className = "select-ui__item"
            if (option.selected) item.classList.add("is-selected")
            item.append(...this.#itemNodes(option))
            item.addEventListener("click", () => this.#pick(option))
            batch.appendChild(item)
        })

        const hidden = matches.length - this.maxResultsValue
        if (hidden > 0) batch.appendChild(this.#hint(`… und ${hidden} weitere — weiter tippen`))

        this.list.appendChild(batch)
    }

    #hint(text) {
        const item = document.createElement("li")
        item.className = "select-ui__hint"
        item.textContent = text
        return item
    }

    // Icon and colour pickers were select2 `templateResult` callbacks.
    //
    // Nodes, not an HTML string: option text is user-editable in several of these
    // lists, and classList/style assignment cannot be broken out of the way
    // string interpolation can.
    #itemNodes(option) {
        const nodes = []

        if (this.formatValue === "icon" && option.text) {
            const glyph = document.createElement("i")
            glyph.classList.add("fal", `fa-${option.text}`)
            nodes.push(glyph)
        }

        if (this.formatValue === "color" && option.value) {
            const swatch = document.createElement("figure")
            swatch.className = "select-ui__swatch"
            swatch.style.backgroundColor = option.value
            nodes.push(swatch)
        }

        const label = document.createElement("span")
        label.className = "select-ui__label"
        label.textContent = option.text
        nodes.push(label)

        return nodes
    }

    #pick(option) {
        this.element.value = option.value
        this.#syncLabel()
        this.close()
        this.element.dispatchEvent(new Event("change", { bubbles: true }))
    }

    #syncLabel() {
        const option = this.element.selectedOptions[0]
        this.button.replaceChildren(...(option ? this.#itemNodes(option) : []))
    }

    // ---- open / close ------------------------------------------------------

    toggle() {
        this.open ? this.close() : this.show()
    }

    show() {
        this.open = true
        this.menu.hidden = false
        this.wrapper.classList.add("is-open")
        this.#renderOptions()
        if (this.search) {
            this.search.value = ""
            this.search.focus()
        }
    }

    close() {
        this.open = false
        this.menu.hidden = true
        this.wrapper.classList.remove("is-open")
    }
}
